import React, { useState, useRef, useEffect } from "react";
import {
  MessageCircleQuestion, PlayCircle, ArrowLeft, Send, ArrowUpRight, History, Plus,
  BookOpen, ChevronRight, Layers, FileText, Video, Newspaper, Download, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import LmsSection from "./LmsSection";
import { ChatText } from "./inline";

const API = process.env.REACT_APP_BACKEND_URL;

/* ---------- YouTube thumbnail helper ---------- */
const ytIdOf = (url) => (url?.match(/(?:v=|embed\/|youtu\.be\/|shorts\/)([\w-]{6,})/) || [])[1];

/* ---------- Resource card renderers ---------- */

function ModuleCard({ r, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rec-module-${r.id}`}
            className="w-full tribal-card p-4 text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
             style={{ background: "linear-gradient(135deg, #B5793F 0%, #D4A574 100%)" }}>
          <BookOpen size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">{r.title}</p>
          <p className="text-[12px] tribal-muted mt-0.5">
            {r.sub_count} sections · {r.lesson_count} lessons
          </p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-[#B5793F] font-semibold bg-[#B5793F20] px-2 py-0.5 rounded-full mt-1">Module</span>
      </div>
    </button>
  );
}

function SubModuleCard({ r, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rec-submodule-${r.id}`}
            className="w-full tribal-card p-4 text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-[#B5793F15]">
          <Layers size={16} className="text-[#B5793F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">{r.title}</p>
          <p className="text-[11px] tribal-muted mt-0.5 truncate">
            {r.module_title} · {r.lesson_count} lesson{r.lesson_count !== 1 ? "s" : ""}
          </p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wide tribal-text font-semibold bg-[#B5793F15] px-2 py-0.5 rounded-full mt-1">Section</span>
      </div>
    </button>
  );
}

function LessonCard({ r, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rec-lesson-${r.id}`}
            className="w-full tribal-card p-4 text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-[#B5793F15]">
          <FileText size={16} className="text-[#B5793F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">{r.title}</p>
          <p className="text-[11px] tribal-muted mt-0.5 truncate">
            {r.module_title}{r.submodule_title ? ` › ${r.submodule_title}` : ""}
          </p>
          {r.videos?.length > 0 && (
            <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-[#B5793F]">
              <Video size={10} /> Has video
            </span>
          )}
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wide tribal-text font-semibold bg-[#B5793F15] px-2 py-0.5 rounded-full mt-1">Lesson</span>
      </div>
    </button>
  );
}

function VideoCard({ r, onClick }) {
  const vid = ytIdOf(r.url);
  return (
    <button onClick={onClick} data-testid={`rec-video-${vid || r.url}`}
            className="w-full tribal-card overflow-hidden text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      {vid && (
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
          <img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt=""
               className="absolute inset-0 w-full h-full object-cover" style={{ filter: "sepia(0.15) brightness(0.9)" }} />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div className="w-12 h-12 rounded-full bg-[#B5793F]/90 flex items-center justify-center">
              <PlayCircle size={24} className="text-white ml-0.5" />
            </div>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {!vid && <Video size={16} className="text-[#B5793F] shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">
              {r.lesson_title || "Video"}
            </p>
            {r.module_title && <p className="text-[11px] tribal-muted mt-0.5 truncate">{r.module_title}</p>}
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-red-400 font-semibold bg-red-900/30 px-2 py-0.5 rounded-full mt-1">Video</span>
        </div>
      </div>
    </button>
  );
}

function ReelCard({ r, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rec-reel-${r.id}`}
            className="w-full tribal-card p-4 text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      <div className="flex items-start gap-3">
        {r.thumbnail ? (
          <img src={r.thumbnail.startsWith("/api/") ? `${API}${r.thumbnail}` : r.thumbnail}
               alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" style={{ filter: "sepia(0.15)" }} />
        ) : (
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-purple-900/30">
            <PlayCircle size={16} className="text-purple-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">{r.title}</p>
          <p className="text-[11px] tribal-muted mt-1 italic">Explore this and come back to continue our chat</p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-purple-300 font-semibold bg-purple-900/30 px-2 py-0.5 rounded-full mt-1">Reel</span>
      </div>
    </button>
  );
}

function UpdateCard({ r, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rec-update-${r.id}`}
            className="w-full tribal-card p-4 text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-blue-900/30">
          <Newspaper size={16} className="text-blue-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">{r.title}</p>
          {r.excerpt && <p className="text-[12px] tribal-text mt-1 line-clamp-2">{r.excerpt}</p>}
          {r.date && <p className="text-[10px] tribal-muted mt-1">{r.date}</p>}
          <p className="text-[11px] tribal-muted mt-1 italic">Explore this and come back to continue our chat</p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-blue-300 font-semibold bg-blue-900/30 px-2 py-0.5 rounded-full mt-1">Update</span>
      </div>
    </button>
  );
}

function DownloadCard({ r, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rec-download-${r.id}`}
            className="w-full tribal-card p-4 text-left group"
            style={{ marginTop: "8px", marginBottom: "4px" }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 bg-green-900/30">
          <Download size={16} className="text-green-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#F1E7D1] group-hover:text-[#B5793F] transition-colors truncate">{r.title}</p>
          {r.description && <p className="text-[12px] tribal-text mt-1 line-clamp-2">{r.description}</p>}
          <p className="text-[11px] tribal-muted mt-1 italic">Explore this and come back to continue our chat</p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-green-400 font-semibold bg-green-900/30 px-2 py-0.5 rounded-full mt-1">Resource</span>
      </div>
    </button>
  );
}

/* ---------- Resource list renderer ---------- */
function ResourceList({ resources, onNavigate }) {
  if (!resources?.length) return null;
  return (
    <div className="mt-2 ml-0 max-w-[85%] md:max-w-[75%] space-y-1" data-testid="resource-recommendations">
      {resources.map((r, i) => {
        const key = r.id || r.url || i;
        switch (r.type) {
          case "module":
            return <ModuleCard key={key} r={r} onClick={() => onNavigate(r)} />;
          case "submodule":
            return <SubModuleCard key={key} r={r} onClick={() => onNavigate(r)} />;
          case "lesson":
            return <LessonCard key={key} r={r} onClick={() => onNavigate(r)} />;
          case "video":
            return <VideoCard key={key} r={r} onClick={() => onNavigate(r)} />;
          case "reel":
            return <ReelCard key={key} r={r} onClick={() => onNavigate(r)} />;
          case "update":
            return <UpdateCard key={key} r={r} onClick={() => onNavigate(r)} />;
          case "download":
            return <DownloadCard key={key} r={r} onClick={() => onNavigate(r)} />;
          default:
            return null;
        }
      })}
    </div>
  );
}

/* ---------- Adaptive learning: AI chat tutor ---------- */
function AdaptiveChat({ onOpenResource, onNavigateTab }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("tribe_learn_session") || null);
  const [sessions, setSessions] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const bottomRef = useRef(null);

  const loadHistory = (id) => {
    fetch(`${API}/api/learn/chat/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        const msgs = (d.messages || []).map((m) => ({
          ...m,
          // Merge resources from new format + backward compat
          resources: m.resources || m.recommended_modules?.map((mod) => ({ type: "module", ...mod })) || [],
        }));
        setMessages(msgs);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (sessionId) loadHistory(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openHistory = async () => {
    setShowHistory((s) => !s);
    try {
      const r = await fetch(`${API}/api/learn/sessions`, { credentials: "include" });
      if (r.ok) setSessions((await r.json()).sessions);
    } catch {}
  };

  const resumeSession = (id) => {
    setSessionId(id);
    localStorage.setItem("tribe_learn_session", id);
    setShowHistory(false);
    loadHistory(id);
  };

  const newSession = () => {
    setSessionId(null);
    localStorage.removeItem("tribe_learn_session");
    setMessages([]);
    setShowHistory(false);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleNavigate = (resource) => {
    const tribeTypes = ["reel", "update", "download"];
    if (tribeTypes.includes(resource.type) && onNavigateTab) {
      toast("Go explore! Come back here when you're done.", { duration: 4000 });
      // Navigate to the appropriate tribe tab
      const tabMap = { reel: "reels", update: "updates", download: "updates" };
      onNavigateTab(tabMap[resource.type] || "updates");
    } else {
      onOpenResource(resource);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/learn/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      if (!sessionId) {
        setSessionId(d.session_id);
        localStorage.setItem("tribe_learn_session", d.session_id);
      }
      // Combine all resources (modules + deep resources)
      const allResources = d.resources || [];
      // If no resources array but has recommended_modules (backward compat)
      if (!allResources.length && d.recommended_modules?.length) {
        allResources.push(...d.recommended_modules.map((mod) => ({ type: "module", ...mod })));
      }
      setMessages((m) => [...m, {
        role: "assistant",
        text: d.response,
        resources: allResources,
      }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Hmm, I hit a snag — please try that again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tribal-card flex flex-col" style={{ height: "min(72vh, 680px)", borderRadius: "12px" }} data-testid="adaptive-chat">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#B5793F20]">
        <p className="text-[11px] uppercase tracking-[0.16em] text-[#C6A97A] font-medium">Your learning assistant</p>
        <div className="flex items-center gap-2">
          <button onClick={newSession} data-testid="chat-new-session-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border border-[#B5793F30] tribal-text hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
            <Plus size={12} /> New chat
          </button>
          <button onClick={openHistory} data-testid="chat-history-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border border-[#B5793F30] tribal-text hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
            <History size={12} /> Past sessions
          </button>
        </div>
      </div>
      {showHistory && (
        <div className="border-b border-[#B5793F20] max-h-44 overflow-y-auto" data-testid="chat-sessions-list">
          {sessions === null && <p className="px-5 py-3 text-xs tribal-muted">Loading…</p>}
          {sessions?.length === 0 && <p className="px-5 py-3 text-xs tribal-muted italic">No past sessions yet.</p>}
          {(sessions || []).map((s) => (
            <button key={s.session_id} onClick={() => resumeSession(s.session_id)} data-testid={`chat-session-${s.session_id}`}
                    className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[#B5793F10] transition-colors"
                    style={{ background: s.session_id === sessionId ? "rgba(181,121,63,0.1)" : "transparent" }}>
              <span className="flex-1 min-w-0 text-[13px] text-[#F1E7D1] truncate">{s.title}</span>
              <span className="shrink-0 text-[11px] tribal-muted">
                {new Date(s.last_ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-5 md:p-7 space-y-4" data-testid="adaptive-chat-messages">
        {messages.length === 0 && !loading && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <MessageCircleQuestion className="text-[#B5793F] mb-4" size={26} strokeWidth={1.5} />
            <p className="tribal-heading text-xl md:text-2xl font-medium mb-2">Your personal learning assistant</p>
            <p className="text-sm tribal-text max-w-md leading-relaxed" style={{ textWrap: "pretty" }}>
              Ask me anything — I'll find the right module, lesson, video, or resource for you
              from across the entire program.
            </p>
            <div className="flex flex-wrap gap-2 mt-5 justify-center">
              {["I want to learn branding", "How do I use Canva?", "Help me with AI tools"].map((q) => (
                <button key={q} onClick={() => { setInput(q); }}
                  className="px-3 py-1.5 rounded-full text-[12px] font-medium border border-[#B5793F30] tribal-text hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors"
                  data-testid={`chat-suggestion-${q.slice(0,10)}`}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i}>
            <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "text-[#F1E7D1]" : "text-[#A69888] border border-[#B5793F20]"}`}
                   style={m.role === "user" ? { background: "#8D6E63" } : { background: "rgba(42,29,18,0.5)" }}>
                {m.role === "assistant" ? <ChatText text={m.text} /> : m.text}
              </div>
            </div>
            {m.role === "assistant" && m.resources?.length > 0 && (
              <ResourceList resources={m.resources} onNavigate={handleNavigate} />
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl px-4 py-3 text-sm tribal-muted border border-[#B5793F20]" style={{ background: "rgba(42,29,18,0.5)" }}>
              <span className="tribal-heading rp-italic">thinking…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <div className="border-t border-[#B5793F20] p-3 md:p-4 flex items-center gap-3">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about any topic, tool, or skill…"
          data-testid="adaptive-chat-input"
          className="flex-1 bg-transparent outline-none text-sm text-[#F1E7D1] placeholder-[#8A7E6B] px-2"
        />
        <button onClick={send} disabled={loading || !input.trim()} data-testid="adaptive-chat-send"
                aria-label="Send message"
                className="tribal-btn-primary w-10 h-10 rounded-full flex items-center justify-center !p-0 disabled:opacity-40">
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}

/* ---------- Learn section: two modes ---------- */
const LEARN_MODES = [
  {
    id: "adaptive",
    icon: MessageCircleQuestion,
    title: "Adaptive Learning",
    text: "Tell your learning assistant what you want to achieve — it'll find the right modules, lessons, and resources for you.",
  },
  {
    id: "structured",
    icon: PlayCircle,
    title: "Structured Learning",
    text: "Browse modules with video lessons, notes and resources — go in order or jump to what you need.",
  },
];

export default function LearnSection({ onNavigateTab, tribeContent }) {
  const [mode, setMode] = useState(() => localStorage.getItem("tribe_learn_mode") || null);
  const [openModuleId, setOpenModuleId] = useState(null);
  const [openLessonId, setOpenLessonId] = useState(null);

  const switchMode = (m) => {
    setMode(m);
    if (m) localStorage.setItem("tribe_learn_mode", m);
    else localStorage.removeItem("tribe_learn_mode");
  };

  const handleOpenResource = (resource) => {
    switch (resource.type) {
      case "module":
        setOpenModuleId(resource.id);
        setOpenLessonId(null);
        switchMode("structured");
        break;
      case "submodule":
        setOpenModuleId(resource.module_id);
        setOpenLessonId(null);
        switchMode("structured");
        break;
      case "lesson":
        setOpenModuleId(resource.module_id);
        setOpenLessonId(resource.id);
        switchMode("structured");
        break;
      case "video":
        if (resource.module_id) {
          setOpenModuleId(resource.module_id);
          setOpenLessonId(resource.lesson_id || null);
          switchMode("structured");
        }
        break;
      default:
        break;
    }
  };

  if (!mode)
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="learn-options">
        {LEARN_MODES.map(({ id, icon: Icon, title, text }) => (
          <button key={id} onClick={() => switchMode(id)} data-testid={`learn-option-${id}`}
                  className="tribal-card p-8 text-left">
            <Icon className="text-[#B5793F] mb-4" size={22} strokeWidth={1.6} />
            <p className="tribal-heading text-xl md:text-2xl font-medium mb-2">{title}</p>
            <p className="text-sm leading-relaxed tribal-text" style={{ textWrap: "pretty" }}>{text}</p>
            <span className="inline-flex items-center gap-1.5 mt-5 text-[13px] font-medium text-[#B5793F]">
              Start <ArrowUpRight size={14} />
            </span>
          </button>
        ))}
      </div>
    );

  return (
    <div>
      <button onClick={() => { switchMode(null); setOpenModuleId(null); setOpenLessonId(null); }} data-testid="learn-back-btn"
              className="inline-flex items-center gap-2 mb-6 text-sm font-medium tribal-text hover:text-[#F1E7D1] transition-colors">
        <ArrowLeft size={15} /> Learning modes
      </button>
      {mode === "adaptive"
        ? <AdaptiveChat onOpenResource={handleOpenResource} onNavigateTab={onNavigateTab} />
        : <LmsSection initialModuleId={openModuleId} initialLessonId={openLessonId} />}
    </div>
  );
}
